<script lang="ts">
	import type { BlogArticlePage } from '$models/Page/BlogArticlePage';
	import { page as routePage } from '$app/state';
	import BlogBlock from '$components/Block/BlogBlock.svelte';
	import RelatedBlogsCarousel from '$components/Blog/RelatedBlogsCarousel.svelte';
	import BlogHero from '$components/Page/BlogHero.svelte';
	import { getSiteContext } from '$lib/context/site';
	import { jsonLdScriptTag } from '$lib/seo/jsonLd';
	import { buildBlogArticleSchema } from '$lib/seo/schemas/blogArticle';
	import { formatDate } from '$lib/utils';

	// Component properties
	export let page: BlogArticlePage;

	const site = getSiteContext();

	$: blogArticleSchemaHtml = jsonLdScriptTag(
		buildBlogArticleSchema(page, new URL(routePage.url.pathname, site.url), site.name)
	);
</script>

<svelte:head>
	{@html blogArticleSchemaHtml}
</svelte:head>

<BlogHero {page} />

<div class="block-blog-wrapper">
	{#each page.blocks as block, index (index)}
		<BlogBlock {block} />
	{/each}
	<div class="block block--blog">
		<div class="block-container container container-small">
			<div class="blog-article-modified">
				Laatst gewijzigd: {formatDate(new Date(page.modified), site.locale.code)}
			</div>
		</div>
	</div>
</div>

<div class="block">
	<div class="block-container container">
		<RelatedBlogsCarousel title={page.relatedPostsTitle} items={page.relatedPosts} />
	</div>
</div>

<style lang="postcss">
	.blog-article {
		& {
			max-width: 948px;
			margin: 0 auto;
		}
		&-title {
			font-family: var(--blog-item-title-font-family);
			font-weight: var(--blog-item-title-font-weight);
			text-transform: var(--blog-item-title-text-tranform);
			margin-bottom: var(--spacing-2);
			&:not(:last-child) {
				margin-bottom: var(--spacing-3);
			}
		}
		&-subtitle {
			font-family: var(--blog-item-subtitle-font-family);
			font-size: var(--blog-item-subtitle-font-size);
			text-transform: uppercase;
			color: var(--blog-item-subtitle-color);
			font-weight: var(--blog-item-subtitle-font-weight);
			margin-bottom: var(--spacing-5);
		}
		&-header {
			margin-bottom: var(--spacing-6);
		}
		&-content {
			&:not(:last-child) {
				border-bottom: 1px solid var(--border-color);
				padding-bottom: var(--spacing-8);
				margin-bottom: var(--spacing-6);
			}
		}
		&-navigation {
			display: flex;
			justify-content: space-between;
		}
		&-prev,
		&-next {
			position: relative;
			display: inline-block;
			color: var(--color-primary);
			line-height: 1;
			font-size: var(--font-size-1);
			transition: color var(--transition);

			&:hover {
				color: color-mix(in srgb, var(--color-primary) 90%, black);
			}

			&:before,
			&:after {
				position: absolute;
				top: 0;
				display: inline-block;
				top: calc(50% - 1px);
				transform: translateY(-50%);
				border-bottom: 1.5px solid var(--color-primary);
				width: 7px;
				height: 7px;
			}
		}
		&-prev {
			padding-left: 14px;
			margin-right: var(--spacing-5);
			&:before {
				content: '';
				left: 0;
				border-left: 1.5px solid var(--color-primary);
				transform: rotate(45deg) translateY(-50%);
			}
		}
		&-next {
			padding-right: 14px;
			margin-left: auto;
			text-align: right;

			&:after {
				content: '';
				right: 0;
				border-right: 1.5px solid var(--color-primary);
				transform: rotate(-45deg) translateY(-50%);
			}
		}
	}
</style>
